import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Plus, Send, ArrowLeft, Clock, CheckCircle, Loader2, AlertCircle, Paperclip, ChevronRight } from 'lucide-react';
import { API } from '@/App';
import ReactMarkdown from 'react-markdown';

const SUBJECTS = [
  { id: 'Question scientifique', icon: '🔬', color: '#059669' },
  { id: 'Adhésion & paiement', icon: '💳', color: '#d97706' },
  { id: 'Annuaire / ma fiche', icon: '📋', color: '#35AFE4' },
  { id: 'Problème technique', icon: '🔧', color: '#dc2626' },
  { id: 'Autre', icon: '💬', color: '#64748b' },
];

const STATUS_MAP = {
  open: { label: 'Ouvert', color: '#059669', bg: '#ecfdf5', icon: AlertCircle },
  in_progress: { label: 'En cours', color: '#d97706', bg: '#fffbeb', icon: Clock },
  resolved: { label: 'Résolu', color: '#64748b', bg: '#f1f5f9', icon: CheckCircle },
};

export default function MemberMessagingPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversations = async () => {
    try {
      const res = await axios.get(`${API}/messaging/conversations`, { headers });
      setConversations(res.data);
    } catch { }
    setLoading(false);
  };

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setShowNew(false);
    try {
      const res = await axios.get(`${API}/messaging/conversations/${conv.id}`, { headers });
      setMessages(res.data.messages);
      setActiveConv(res.data.conversation);
      // Refresh list to update unread counts
      loadConversations();
    } catch { }
  };

  const createConversation = async () => {
    if (!newSubject || !newTitle.trim() || !newMessage.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messaging/conversations`, {
        subject: newSubject, title: newTitle.trim(), message: newMessage.trim(),
      }, { headers });
      setShowNew(false);
      setNewSubject(''); setNewTitle(''); setNewMessage('');
      await loadConversations();
      openConversation(res.data);
    } catch { }
    setSending(false);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeConv) return;
    setSending(true);
    try {
      await axios.post(`${API}/messaging/conversations/${activeConv.id}/messages`, {
        content: replyText.trim(),
      }, { headers });
      setReplyText('');
      const res = await axios.get(`${API}/messaging/conversations/${activeConv.id}`, { headers });
      setMessages(res.data.messages);
      setActiveConv(res.data.conversation);
      loadConversations();
    } catch { }
    setSending(false);
  };

  const getSubjectInfo = (subj) => SUBJECTS.find(s => s.id === subj) || SUBJECTS[4];

  // ─── New conversation form ──────────────────────────────────────────────
  const NewConversationForm = () => (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="new-conversation-form">
      <button onClick={() => setShowNew(false)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} /> Retour
      </button>
      <h2 className="text-lg font-bold text-gray-900 mb-4">Nouvelle demande</h2>

      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mb-2 block">Sujet</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SUBJECTS.map(s => (
            <button key={s.id} onClick={() => setNewSubject(s.id)}
              data-testid={`subject-${s.id}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${
                newSubject === s.id ? 'border-[#35AFE4] bg-[#35AFE4]/5 ring-1 ring-[#35AFE4]' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <span className="text-lg">{s.icon}</span>
              <span className="font-medium">{s.id}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mb-1 block">Titre</label>
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
          placeholder="Résumez votre demande en quelques mots"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4]"
          data-testid="new-conv-title" />
      </div>

      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mb-1 block">Votre message</label>
        <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
          rows={5} placeholder="Décrivez votre demande en détail..."
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] resize-none"
          data-testid="new-conv-message" />
      </div>

      <button onClick={createConversation}
        disabled={!newSubject || !newTitle.trim() || !newMessage.trim() || sending}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-40 transition-colors"
        data-testid="create-conv-btn">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Envoyer
      </button>
    </div>
  );

  // ─── Conversation thread ────────────────────────────────────────────────
  const ConversationThread = () => {
    if (!activeConv) return null;
    const subj = getSubjectInfo(activeConv.subject);
    const status = STATUS_MAP[activeConv.status] || STATUS_MAP.open;
    const StatusIcon = status.icon;

    return (
      <div className="flex flex-col h-full" data-testid="conversation-thread">
        {/* Header */}
        <div className="border-b border-gray-100 p-4 flex items-center gap-3">
          <button onClick={() => { setActiveConv(null); setMessages([]); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 lg:hidden">
            <ArrowLeft size={18} />
          </button>
          <span className="text-xl">{subj.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">{activeConv.title}</h3>
            <p className="text-xs text-gray-500">{activeConv.subject}</p>
          </div>
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold" style={{ color: status.color, backgroundColor: status.bg }}>
            <StatusIcon size={12} /> {status.label}
          </span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map(msg => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                  isMe ? 'bg-[#1e3a5f] text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'
                }`}>
                  {!isMe && <p className="text-[10px] font-bold mb-1 opacity-70">{msg.sender_name} · Équipe SFOALC</p>}
                  <div className={isMe ? '' : 'prose prose-sm max-w-none'}>
                    {isMe ? msg.content : <ReactMarkdown>{msg.content}</ReactMarkdown>}
                  </div>
                  <p className={`text-[9px] mt-1 ${isMe ? 'text-white/50' : 'text-gray-400'}`}>
                    {new Date(msg.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEnd} />
        </div>

        {/* Reply input */}
        {activeConv.status !== 'resolved' ? (
          <div className="border-t border-gray-100 p-3">
            <div className="flex items-end gap-2">
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Votre réponse..." rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30"
                data-testid="reply-input" disabled={sending} />
              <button onClick={sendReply} disabled={!replyText.trim() || sending}
                className="p-2.5 rounded-xl bg-[#1e3a5f] text-white hover:bg-[#162d4a] disabled:opacity-40 transition-colors"
                data-testid="reply-send-btn">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-100 p-3 text-center text-sm text-gray-400">
            <CheckCircle size={16} className="inline mr-1" /> Cette conversation est résolue
          </div>
        )}
      </div>
    );
  };

  // ─── Main layout ────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="messaging-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Messagerie</h1>
            <p className="text-sm text-gray-500">Échangez avec l'équipe SFOALC</p>
          </div>
          {!showNew && !activeConv && (
            <button onClick={() => setShowNew(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors"
              data-testid="new-request-btn">
              <Plus size={16} /> Nouvelle demande
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden min-h-[500px]">
          {showNew ? (
            NewConversationForm()
          ) : activeConv ? (
            ConversationThread()
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <MessageSquare size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm mb-4">Aucune conversation pour le moment</p>
              <button onClick={() => setShowNew(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#35AFE4] text-white text-sm font-medium hover:bg-[#2a9cd4] transition-colors"
                data-testid="first-request-btn">
                <Plus size={16} /> Envoyer votre première demande
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map(conv => {
                const subj = getSubjectInfo(conv.subject);
                const status = STATUS_MAP[conv.status] || STATUS_MAP.open;
                return (
                  <button key={conv.id} onClick={() => openConversation(conv)}
                    data-testid={`conv-${conv.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors">
                    <span className="text-xl flex-shrink-0">{subj.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{conv.title}</p>
                        {conv.unread_member > 0 && (
                          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#35AFE4] text-white text-[9px] font-bold">
                            {conv.unread_member}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{conv.last_message_preview}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ color: status.color, backgroundColor: status.bg }}>
                        {status.label}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(conv.last_message_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
  );
}
